import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { KeyRound, ArrowLeft } from "lucide-react";
import { LOGO_URL } from "../lib/brand";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw1 !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: pw1,
      });
      // Auto sign-in on success
      localStorage.setItem("cb_token", data.access_token);
      toast.success("Password reset. You're signed in.");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-10">
        <div className="nb-card-lg p-8 text-center max-w-md" data-testid="reset-invalid">
          <div className="font-display font-black text-2xl mb-2">Invalid reset link</div>
          <div className="text-sm font-medium text-[#4a4a4a] mb-4">
            This URL is missing its reset token.
          </div>
          <Link to="/forgot-password" className="nb-btn nb-btn-dark inline-flex">
            Request a new one
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute -top-24 -left-16 w-72 h-72 rounded-full bg-[#d9d4f1] opacity-70 blur-2xl" />
      <div className="absolute -bottom-24 -right-16 w-80 h-80 rounded-full bg-[#b5e4ca] opacity-60 blur-2xl" />
      <div className="relative w-full max-w-md">
        <div className="mb-6 text-center">
          <img
            src={LOGO_URL}
            alt="SyncBook"
            className="mx-auto w-full max-w-[240px] rounded-2xl border-2 border-[#1a1a1a] shadow-[4px_4px_0_0_#1a1a1a] mb-4"
          />
          <h1 className="font-display text-4xl font-black tracking-tight">Choose a new password</h1>
          <p className="text-sm text-[#4a4a4a] mt-2 font-medium">Make it a good one.</p>
        </div>

        <form onSubmit={submit} className="nb-card-lg p-6 space-y-4" data-testid="reset-password-form">
          <div>
            <label className="label">New password</label>
            <input
              data-testid="new-password-input"
              type="password"
              required
              minLength={6}
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              className="nb-input"
              placeholder="At least 6 characters"
            />
          </div>
          <div>
            <label className="label">Confirm new password</label>
            <input
              data-testid="confirm-password-input"
              type="password"
              required
              minLength={6}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="nb-input"
              placeholder="Type it again"
            />
          </div>
          <button
            data-testid="reset-submit-btn"
            disabled={busy}
            type="submit"
            className="nb-btn nb-btn-dark w-full !py-3"
          >
            <KeyRound size={18} strokeWidth={3} /> {busy ? "Resetting…" : "Reset password & sign in"}
          </button>
          <Link
            to="/login"
            data-testid="back-to-login"
            className="nb-btn nb-btn-secondary w-full !py-2 justify-center"
          >
            <ArrowLeft size={14} strokeWidth={3} /> Back to sign in
          </Link>
        </form>
      </div>
    </div>
  );
}
